
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, User } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger
} from '@/components/ui/navigation-menu';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Updated service items with correct section IDs
  const serviceItems = [
    { name: 'Or', href: '#gold-section' },
    { name: 'Assurance vie luxembourgeoise', href: '#luxembourg-insurance' },
    { name: 'Actions Boursière', href: '#stock-section' },
    { name: 'Eco Park', href: '#services-section' },
    { name: 'Hydrogène', href: '#hydrogen-section' },
    { name: 'Crypto', href: '#crypto-section' }
  ];

  // Function to handle navigation to sections, redirecting to homepage if on another page
  const navigateToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    
    // If we're not on the homepage, navigate to home with the section hash
    if (location.pathname !== '/') {
      navigate(`/${sectionId}`);
    } else {
      // If already on homepage, just scroll to the section
      const element = document.getElementById(sectionId.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    setIsMenuOpen(false);
  };

  return (
    <header 
      className="w-full fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md py-2"
    >
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <div className="h-8 md:h-10 flex items-center">
            <img 
              src="/lovable-uploads/a7684a94-1885-45e4-828f-e79f3dfa409a.png" 
              alt="Logo LA PATRIMONIALE" 
              className="h-full w-auto object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="ml-2 text-xl md:text-2xl font-serif font-semibold text-patrimoine-dark flex items-center self-center my-auto">
              LA PATRIMONIALE
            </span>
            <span className="ml-2 text-xs font-serif text-patrimoine-gold text-center w-full">
              GESTION PRIVÉE
            </span>
          </div>
        </Link>
        
        {/* Mobile menu button */}
        <button 
          className="md:hidden text-patrimoine-gold"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        
        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className={`hover:text-patrimoine-gold transition-colors font-medium text-patrimoine-muted`}>
            Accueil
          </Link>
          <Link to="/a-propos" className={`hover:text-patrimoine-gold transition-colors font-medium text-patrimoine-muted`}>
            À Propos
          </Link>
          
          {/* Services dropdown menu - changed to "Nos Produits" - corrected font style */}
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="hover:text-patrimoine-gold transition-colors font-medium text-patrimoine-muted">Nos Produits</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[300px] p-3 gap-2 bg-white">
                    {serviceItems.map((item) => (
                      <li key={item.name}>
                        <NavigationMenuLink asChild>
                          <a 
                            href={item.href}
                            onClick={(e) => navigateToSection(e, item.href)}
                            className="block select-none space-y-1 rounded-md p-3 hover:bg-accent hover:text-accent-foreground transition-colors hover:text-patrimoine-gold"
                          >
                            <div className="text-sm font-medium leading-none">
                              {item.name}
                            </div>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          
          <Link to="/approche" className={`hover:text-patrimoine-gold transition-colors font-medium text-patrimoine-muted`}>
            Notre Approche
          </Link>
          <Link to="/contact" className={`hover:text-patrimoine-gold transition-colors font-medium text-patrimoine-muted`}>
            Contact
          </Link>
          <Button className="cta-button flex items-center font-medium bg-patrimoine-gold text-black hover:bg-amber-500">
            <User className="mr-2 h-4 w-4" />
            Espace Client
          </Button>
        </nav>
        
        {/* Mobile navigation */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg p-6 md:hidden">
            <nav className="flex flex-col space-y-5">
              <Link 
                to="/" 
                className="text-patrimoine-DEFAULT hover:text-patrimoine-gold transition-colors px-2 py-1 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Accueil
              </Link>
              <Link 
                to="/a-propos" 
                className="text-patrimoine-DEFAULT hover:text-patrimoine-gold transition-colors px-2 py-1 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                À Propos
              </Link>
              
              {/* Services dropdown items for mobile - changed to "Nos Produits" */}
              <div className="space-y-2 pl-4">
                <div className="text-patrimoine-DEFAULT font-medium px-2 py-1">Nos Produits</div>
                {serviceItems.map((item) => (
                  <a 
                    key={item.name}
                    href={item.href}
                    onClick={(e) => navigateToSection(e, item.href)}
                    className="block text-patrimoine-DEFAULT hover:text-patrimoine-gold transition-colors px-4 py-1 text-sm"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              
              <Link 
                to="/approche" 
                className="text-patrimoine-DEFAULT hover:text-patrimoine-gold transition-colors px-2 py-1 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Notre Approche
              </Link>
              <Link 
                to="/contact" 
                className="text-patrimoine-DEFAULT hover:text-patrimoine-gold transition-colors px-2 py-1 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Button 
                className="cta-button w-full text-center flex justify-center items-center font-medium mt-4 bg-patrimoine-gold text-black hover:bg-amber-500"
                onClick={() => setIsMenuOpen(false)}
              >
                <User className="mr-2 h-4 w-4" />
                Espace Client
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
